import { DateTime } from "luxon"
import { getNamedPartecipants } from "./func.utils"
import { regularPartecipants, substituteOf } from "./substitutes.utils"
import { heldLessons } from "./cancellation.utils"
import { withoutMatches } from "./match.utils"

// ──────────────────────────────────────────────────────────────────────────
// LA SETTIMANA DELLE LEZIONI
//
// La settimana va da lunedì a domenica, come la pensa chi organizza un
// circolo: il weekend chiude la settimana, non la apre. Tutto qui lavora
// sulle date ISO ("2026-09-29") che la cache delle lezioni usa come chiave.
// ──────────────────────────────────────────────────────────────────────────

export const LESSONS_VIEWS = ["day", "week"]

// Il lunedì e la domenica della settimana che contiene il giorno dato
export const getWeekBounds = isoDate => {
	const day = DateTime.fromISO(isoDate)
	return {
		start: day.startOf("week").toISODate(),
		end: day.endOf("week").toISODate(),
	}
}

// I sette giorni della settimana, da lunedì
export const getWeekDays = isoDate => {
	const monday = DateTime.fromISO(getWeekBounds(isoDate).start)
	return Array.from({ length: 7 }, (_, i) => monday.plus({ days: i }).toISODate())
}

// "28 set – 4 ott", o "5 – 11 ott" quando il mese è lo stesso
export const getWeekRangeLabel = isoDate => {
	const { start, end } = getWeekBounds(isoDate)
	const from = DateTime.fromISO(start).setLocale("it")
	const to = DateTime.fromISO(end).setLocale("it")
	return from.month === to.month
		? `${from.day} – ${to.toFormat("d LLL")}`
		: `${from.toFormat("d LLL")} – ${to.toFormat("d LLL")}`
}

// Come si chiama un giorno nell'agenda. Ieri, oggi e domani si dicono per
// nome, perché è così che li si pensa, e la data resta di fianco per chi
// la cerca; gli altri giorni sono il loro nome e il numero.
export const getDayTitle = (isoDate, now = DateTime.now()) => {
	const day = DateTime.fromISO(isoDate).setLocale("it")
	const diff = Math.round(day.startOf("day").diff(now.startOf("day"), "days").days)
	const relative = { "-1": "Ieri", 0: "Oggi", 1: "Domani" }[diff]
	const weekday = day.toFormat("cccc")

	if (relative) return { title: relative, aside: day.toFormat("ccc d"), isToday: diff === 0 }
	return {
		title: `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${day.day}`,
		aside: null,
		isToday: false,
	}
}

const minutesOf = timestamp => {
	const time = DateTime.fromJSDate(timestamp.toDate())
	return time.hour * 60 + time.minute
}

export const lessonStartMinutes = lesson => minutesOf(lesson.lessonStart)
export const lessonEndMinutes = lesson => minutesOf(lesson.lessonEnd)

// Le ore di lezione di un giorno. Due lezioni che si accavallano — una da
// titolare e una da assistente, per dire — contano per il tempo che
// occupano davvero, non due volte.
export const getBookedMinutes = lessons => {
	let total = 0
	let reachedUntil = -1
	// una lezione annullata resta in agenda, ma quel tempo è libero
	;(lessons ?? [])
		.filter(lesson => lesson?.cancelled !== true)
		.sort((a, b) => lessonStartMinutes(a) - lessonStartMinutes(b))
		.forEach(lesson => {
			const start = Math.max(lessonStartMinutes(lesson), reachedUntil)
			const end = lessonEndMinutes(lesson)
			if (end > start) total += end - start
			reachedUntil = Math.max(reachedUntil, end)
		})
	return total
}

export const formatHours = minutes =>
	`${(minutes / 60).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h`

// Le ore come si dicono, per il riepilogo in cima al telefono: "1 ora",
// "3 ore", e la mezz'ora col ½ — "4½ ore" si legge "quattro e mezza" e sta
// in una riga corta. Quello che non è né intero né mezzo (lezioni da 45
// minuti, per dire) resta preciso: "3 h 45".
export const formatHoursSpoken = minutes => {
	const hours = Math.floor(minutes / 60)
	const rest = minutes % 60
	if (rest === 0) return hours === 1 ? "1 ora" : `${hours} ore`
	if (rest === 30) return hours === 0 ? "½ ora" : `${hours}½ ore`
	if (hours === 0) return `${rest} min`
	return `${hours} h ${String(rest).padStart(2, "0")}`
}

// Le stesse parole per le ore che arrivano già in ore, come quelle del
// profilo: sommando lezioni da 20 minuti viene 6,333…, che scritto così è un
// conto, non un orario. Si torna ai minuti interi e si dice "6 h 20".
export const formatHoursAmount = hours => formatHoursSpoken(Math.round((Number(hours) || 0) * 60))

// Le lezioni che si contano come lavoro: non le annullate, e non le partite,
// che in agenda ci stanno ma sono tempo tuo.
export const workedLessons = lessons => heldLessons(withoutMatches(lessons))

// "4 lezioni · 4 ore" per un giorno o per una settimana: le lezioni arrivano
// divise per giorno, perché le ore si contano giorno per giorno (vedi
// getBookedMinutes). Le annullate e le partite non contano. Senza lezioni non
// c'è niente da dire: null, e in cima resta solo il logo.
export const getLessonsSummary = lessonsByDay => {
	const days = (lessonsByDay ?? []).map(lessons => workedLessons(lessons))
	const count = days.reduce((sum, lessons) => sum + lessons.length, 0)
	if (count === 0) return null
	const minutes = days.reduce((sum, lessons) => sum + getBookedMinutes(lessons), 0)
	return `${count === 1 ? "1 lezione" : `${count} lezioni`} · ${formatHoursSpoken(minutes)}`
}

export const formatMinutes = minutes =>
	`${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`

// In ordine di ora d'inizio, senza toccare l'elenco di partenza
export const sortByStart = lessons =>
	[...(lessons ?? [])].sort((a, b) => a.lessonStart.toDate() - b.lessonStart.toDate())

// ──────────────────────────────────────────────────────────────────────────
// LE ORE NEGLI ALTRI CIRCOLI
//
// Chi insegna in più circoli può vedere, accanto alle lezioni di quello che
// sta guardando, quelle che ha altrove: non per lavorarci — si modificano
// dal loro circolo — ma per sapere quando è già impegnato.
// ──────────────────────────────────────────────────────────────────────────

// Una lezione di un altro circolo, rispetto a quello che si sta guardando
export const isElsewhere = (lesson, schoolID) =>
	Boolean(lesson?.schoolID) && lesson.schoolID !== schoolID

// Le lezioni del circolo e quelle altrove in un elenco solo, nell'ordine in
// cui succedono
export const withLessonsElsewhere = (lessons, elsewhere) =>
	sortByStart([...(lessons ?? []), ...(elsewhere ?? [])])

// "+2 altrove", accanto alle ore del giorno; niente se non ce ne sono
export const formatElsewhere = count => (count > 0 ? `+${count} altrove` : null)

// Due lezioni una dopo l'altra, senza un minuto in mezzo, stanno attaccate:
// come nell'elenco del giorno, dove la seconda comincia quando finisce la
// prima. Vale anche per due lezioni di seguito nello stesso altro circolo.
// Una lezione altrove resta invece staccata da una di qui, e da una di un
// terzo circolo, anche se l'orario combacia: attaccate sembrerebbero un
// pezzo dello stesso blocco. "Gioco io" è del circolo che si guarda come le
// lezioni di qui, e si attacca come loro. Torna, per ognuna, se è attaccata
// a quella prima e a quella dopo.
export const getLessonJoins = (lessons, schoolID) => {
	const list = lessons ?? []
	const sameSchool = (a, b) =>
		isElsewhere(a, schoolID) || isElsewhere(b, schoolID) ? a.schoolID === b.schoolID : true
	const joins = (a, b) =>
		Boolean(a && b) && sameSchool(a, b) && lessonEndMinutes(a) === lessonStartMinutes(b)

	return list.map((lesson, i) => ({
		joinsPrev: joins(list[i - 1], lesson),
		joinsNext: joins(lesson, list[i + 1]),
	}))
}

// L'elenco del giorno com'è disegnato: ogni lezione di qui per conto suo,
// con quello che le sta attaccato (`joins`) e il suo posto nell'elenco
// (`index`); le lezioni di seguito nello stesso altro circolo invece in un
// pezzo solo, un blocco tratteggiato con una riga per lezione. Due righe
// staccate, ognuna col suo tratteggio, a 4px una dall'altra facevano una
// cucitura doppia, coi trattini sfalsati.
export const groupLessonsElsewhere = (lessons, schoolID) => {
	const list = lessons ?? []
	const joins = getLessonJoins(list, schoolID)

	return list.reduce((items, lesson, i) => {
		const last = items[items.length - 1]
		if (isElsewhere(lesson, schoolID) && joins[i].joinsPrev) {
			last.lessons.push(lesson)
			last.joins = { ...last.joins, joinsNext: joins[i].joinsNext }
		} else items.push({ lessons: [lesson], joins: joins[i], index: i })
		return items
	}, [])
}

// Una lezione dentro un intervallo dello stesso giorno, in minuti: si
// toccano se una comincia prima che l'altra finisca. Due lezioni una dopo
// l'altra non si sovrappongono.
export const overlapMinutes = (lesson, from, to) =>
	Math.max(0, Math.min(lessonEndMinutes(lesson), to) - Math.max(lessonStartMinutes(lesson), from))

// Le lezioni di un giorno che toccano un intervallo, con per quanto: quelle
// che il modulo deve dire prima di salvare. Le annullate non occupano né il
// maestro né il campo, e la lezione che si sta modificando non si accavalla
// con sé stessa.
export const overlapsWithin = (lessons, from, to, exceptLessonID = null) =>
	heldLessons(lessons)
		.filter(lesson => !exceptLessonID || lesson.lessonID !== exceptLessonID)
		.map(lesson => ({ lesson, minutes: overlapMinutes(lesson, from, to) }))
		.filter(({ minutes }) => minutes > 0)

// Nell'elenco del giorno, per ogni lezione che si accavalla con un'altra
// dello stesso elenco, con quali. Le annullate non contano da nessuna delle
// due parti: non si fanno.
export const getLessonOverlaps = lessons => {
	const overlaps = new Map()
	heldLessons(lessons).forEach((lesson, _, held) => {
		const others = overlapsWithin(
			held,
			lessonStartMinutes(lesson),
			lessonEndMinutes(lesson),
			lesson.lessonID,
		).map(({ lesson: other }) => other)
		if (others.length > 0) overlaps.set(lesson.lessonID, others)
	})
	return overlaps
}

// Chi c'è in una lezione, in una riga: "Anna Bianchi", "Anna Bianchi e Luca
// Ferri", "Anna Bianchi e altri 3". Gli assenti no, i posti senza nome
// nemmeno; senza nessuno, null.
export const lessonPeopleInLine = lesson => {
	const names = getNamedPartecipants(lesson?.partecipants)
		.filter(partecipant => partecipant.isPresent !== false)
		.map(partecipant => partecipant.playerName?.trim())
		.filter(Boolean)
	if (names.length === 0) return null
	if (names.length === 1) return names[0]
	if (names.length === 2) return `${names[0]} e ${names[1]}`
	return `${names[0]} e altri ${names.length - 1}`
}

// Nella griglia due lezioni che si accavallano stanno una di fianco
// all'altra. A ciascuna la sua corsia, e a ciascun gruppo di lezioni che si
// toccano il numero di corsie che gli servono.
export const assignLanes = lessons => {
	const sorted = [...(lessons ?? [])].sort(
		(a, b) => lessonStartMinutes(a) - lessonStartMinutes(b),
	)
	const placed = []
	let group = []
	let groupEnd = -1
	let lanesEnd = []

	const closeGroup = () => {
		const lanes = lanesEnd.length
		group.forEach(item => (item.lanes = lanes))
		group = []
		lanesEnd = []
	}

	sorted.forEach(lesson => {
		const start = lessonStartMinutes(lesson)
		const end = lessonEndMinutes(lesson)
		if (start >= groupEnd) closeGroup()

		let lane = lanesEnd.findIndex(laneEnd => laneEnd <= start)
		if (lane === -1) lane = lanesEnd.length
		lanesEnd[lane] = end

		const item = { lesson, lane, lanes: 1 }
		group.push(item)
		placed.push(item)
		groupEnd = Math.max(groupEnd, end)
	})
	closeGroup()

	return placed
}

// La fascia che la griglia mostra, in minuti dalla mezzanotte. Sempre la
// stessa, così una settimana non cambia forma rispetto alla precedente: le
// lezioni cominciano dalle 8 e finiscono entro le 23, e un'ora prima e
// mezz'ora dopo sono il margine. Si allarga solo se qualche lezione ne esce.
export const GRID_RANGE = { from: 7 * 60, to: 23 * 60 + 30 }

export const getGridRange = (lessonsByDate, defaults = GRID_RANGE) => {
	let from = defaults.from
	let to = defaults.to
	Object.values(lessonsByDate ?? {}).forEach(dayLessons =>
		(dayLessons ?? []).forEach(lesson => {
			from = Math.min(from, Math.floor(lessonStartMinutes(lesson) / 60) * 60)
			to = Math.max(to, Math.ceil(lessonEndMinutes(lesson) / 30) * 30)
		}),
	)
	return { from, to }
}

// Chi c'è in campo, come nella scheda della lezione: il sostituto al posto
// di chi manca, e i posti senza nome contati a parte.
export const getLessonPlayersText = lesson => {
	const regulars = regularPartecipants(lesson.partecipants ?? [])
	const named = getNamedPartecipants(regulars)
	const placeholders = regulars.length - named.length

	const names = named.map(player => {
		const substitute =
			player.isPresent === false ? substituteOf(lesson.partecipants, player) : null
		return (substitute ?? player).playerName?.trim()
	})
	if (placeholders > 0)
		names.push(placeholders === 1 ? "1 posto da segnare" : `${placeholders} posti da segnare`)

	return names.filter(Boolean).join(", ")
}
